import { AppModelStateEditStreamShape } from 'appModelStateEdit/types';
import {
  ModifySimplePropertyPayload,
  ModifyTagsPayload,
  TagNamesPayload,
  addNewTags,
  clearFormError,
  modifyReferenceTargetProperty,
  modifySimpleProperty,
  modifyTags,
  removeNewTags,
  setAttributeDirty,
  setFormError,
} from 'appModelStateEdit/propertiesEditor/actions';
import { updateScopeDataProperties } from 'appModelStateEdit/reducerUtils';
import { reducer } from '@ardoq/rxbeach';
import { reducers as componentPropertiesEditorReducers } from 'appModelStateEdit/propertiesEditor/componentPropertiesEditor/reducers';
import {
  addNewTagsToScopeData,
  applyTagsToEntities,
  removeNewTagsFromScopeData,
} from 'appModelStateEdit/propertiesEditor/reducerUtils';
import { ArdoqId } from '@ardoq/api-types';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';

const modifySimplePropertyReducer = (
  state: AppModelStateEditStreamShape,
  { propertyName, newValue }: ModifySimplePropertyPayload
) => {
  if (!state.entityType || !state.enhancedScopeData) {
    return { ...state };
  }
  return {
    ...state,
    enhancedScopeData: updateScopeDataProperties(
      state.entityType,
      state.entityIDs,
      state.enhancedScopeData,
      { [propertyName]: newValue },
      state.model
    ),
  };
};

const modifyTagsReducer = (
  state: AppModelStateEditStreamShape,
  currentTags: ModifyTagsPayload
) => {
  if (!state.entityType || !state.enhancedScopeData) {
    return { ...state };
  }
  const newScopeData = applyTagsToEntities(
    state.entityType,
    state.entityIDs,
    state.enhancedScopeData,
    currentTags
  );
  return {
    ...state,
    enhancedScopeData: newScopeData,
  };
};

const addNewTagsReducer = (
  state: AppModelStateEditStreamShape,
  tagNamesToAdd: TagNamesPayload
) => {
  if (!state.entityType || !state.enhancedScopeData) {
    return { ...state };
  }
  const newScopeData = addNewTagsToScopeData(
    state.entityType,
    state.entityIDs,
    state.enhancedScopeData,
    tagNamesToAdd
  );
  const addedTags = new Set([...state.addedTags, ...tagNamesToAdd]);
  return {
    ...state,
    addedTags,
    enhancedScopeData: newScopeData,
  };
};

const removeNewTagsReducer = (
  state: AppModelStateEditStreamShape,
  tagNamesToRemove: TagNamesPayload
) => {
  if (!state.enhancedScopeData) {
    return { ...state };
  }
  const newScopeData = removeNewTagsFromScopeData(
    state.enhancedScopeData,
    tagNamesToRemove
  );
  const addedTags = new Set(state.addedTags);
  tagNamesToRemove.forEach(tagName => addedTags.delete(tagName));
  return {
    ...state,
    addedTags,
    enhancedScopeData: newScopeData,
  };
};

const setFormErrorReducer = (
  state: AppModelStateEditStreamShape,
  errorMessage: string
): AppModelStateEditStreamShape => {
  return {
    ...state,
    errorMessage,
  };
};

const clearFormErrorReducer = (
  state: AppModelStateEditStreamShape
): AppModelStateEditStreamShape => {
  return {
    ...state,
    errorMessage: null,
  };
};

const setAttributeDirtyReducer = (
  state: AppModelStateEditStreamShape,
  attributeName: string
) => {
  if (state.dirtyAttributes.has(attributeName)) {
    return state;
  }
  return {
    ...state,
    dirtyAttributes: new Set([attributeName, ...state.dirtyAttributes]),
  };
};

const modifyReferenceTargetPropertyReducer = (
  state: AppModelStateEditStreamShape,
  newTargetId: ArdoqId
) => {
  if (!state.entityType || !state.enhancedScopeData) {
    return { ...state };
  }
  return {
    ...state,
    enhancedScopeData: updateScopeDataProperties(
      state.entityType,
      state.entityIDs,
      state.enhancedScopeData,
      {
        target: newTargetId,
        targetWorkspace: enhancedScopeDataOperations.getComponentWorkspaceId(
          state.enhancedScopeData,
          newTargetId
        ),
      },
      state.model
    ),
  };
};

const reducers = [
  reducer(modifySimpleProperty, modifySimplePropertyReducer),
  reducer(modifyReferenceTargetProperty, modifyReferenceTargetPropertyReducer),
  reducer(modifyTags, modifyTagsReducer),
  reducer(addNewTags, addNewTagsReducer),
  reducer(removeNewTags, removeNewTagsReducer),
  reducer(setFormError, setFormErrorReducer),
  reducer(clearFormError, clearFormErrorReducer),
  reducer(setAttributeDirty, setAttributeDirtyReducer),
  ...componentPropertiesEditorReducers,
];
export { reducers };
